import { Button, DateRangeBox } from "devextreme-react";
import { Checklist, ChecklistScheduleDto } from "../model/ChecklistDtos";
import ScheduleRow from "./ScheduleRow";
import { useEffect, useState } from "react";
import ChecklistsApi from "../api/ChecklistApi";
import ScheduleForm, { FormState } from "./ScheduleForm";
import { t } from "i18next";
import { useScheduleValidation } from "../hook/useChecklistScheduleValidator";
import { useChecklistScheduleMapper } from "../hook/useChecklistScheduleMapper";
import { useNavigate } from "react-router-dom";
import { IconButtonCalendar, IconButtonDelete, IconButtonGoBack, Modal, Row } from "../../../core/components";

interface SchedulesModalProps {
    isOpen: boolean
    onClose: () => void
    checklist?: Checklist
    schedules: ChecklistScheduleDto[]
    onDeleteSchedule?: () => void
    onSaveSchedule?: () => void
}

export default function SchedulesModal(props: SchedulesModalProps) {
    const [showScheduleDetails, setShowScheduleDetails] = useState(false)
    const [loading, setLoading] = useState(false)
    const [formData, setFormData] = useState<Partial<FormState>>()
    const [filteredSchedules, setFilteredSchedules] = useState<ChecklistScheduleDto[]>(props.schedules || [])
    const [currentFilteredDateRange, setCurrentFilteredDateRange] = useState<[Date | null, Date | null]>([null, null])
    const navigate = useNavigate()

    const { validate, validationErrors, clearValidation } = useScheduleValidation(formData)
    const { toDto, toRequest } = useChecklistScheduleMapper()

    const handleChange = (updates: Partial<FormState>) => {
        if (!formData) return

        setFormData((prev) => ({ ...prev, ...updates }));
        clearValidation(Object.keys(updates) as (keyof FormState)[]);
    };

    const handleEditSchedule = async (idChecklist?: number, idChecklistSchedule?: number) => {
        if (idChecklist && idChecklistSchedule) {
            setShowScheduleDetails(true)
            await fetchSchedule(idChecklist, idChecklistSchedule)
        }
    }

    async function fetchSchedule(idChecklist: number, idChecklistSchedule: number) {
        setLoading(true)
        const schedule = await ChecklistsApi.getSchedule(idChecklist, idChecklistSchedule)
        if (!schedule) {
            return
        }

        setFormData(toDto(schedule))
        setLoading(false)
    }

    const handleCreateNewSchedule = () => {
        setShowScheduleDetails(true)
        const defaultFormState: FormState = {
            idChecklist: props.checklist?.idChecklist,
            onceDate: new Date().toISOString().substring(0, 10),
            startDate: new Date().toISOString().substring(0, 10),
            endDate: undefined,
            enableRepeat: false,
            repeatType: 1,
            repeatOnDayDaily: [],
            repeatOnDayWeekly: 0,
            repeatOnMonths: [],
            repeatOnWeekdayOnMonths: false,
            monthlyFrequencyDay: [],
            monthlyOnWeekDays: [],
            monthlyFrequencyOnWeekDay: [],
        }
        setFormData(defaultFormState)
    }

    const handleSaveChecklistSchedule = async () => {
        if (!validate() || !formData) {
            return
        }
        const idChecklist = formData.idChecklist;
        if (!idChecklist || !formData.startDate) {
            return
        }

        const request = toRequest(formData)
        if (!request) {
            return
        }

        setLoading(true)
        if (!formData.idChecklistSchedule) {
            if (await ChecklistsApi.scheduleChecklist(request)) {
                props.onSaveSchedule?.()
                setShowScheduleDetails(false)
            }
        } else {
            if (await ChecklistsApi.updateChecklistSchedule(request)) {
                props.onSaveSchedule?.()
                setShowScheduleDetails(false)
            }
        }
        setLoading(false)
    }

    const handleDeleteChecklistSchedule = async (idChecklist?: number, idChecklistSchedule?: number) => {
        if (!idChecklist || !idChecklistSchedule) return

        if (await ChecklistsApi.deleteSchedule(idChecklistSchedule)) {
            props.onDeleteSchedule?.()
            setShowScheduleDetails(false)
        }
    }
    const filterSchedulesByDate = (dateRange: [Date | null, Date | null]) => {
        const [start, end] = dateRange;

        const filtered = props.schedules.filter(schedule => !!schedule.startDate).filter(schedule => {
            const scheduleStart = new Date(schedule.startDate!);
            const scheduleEnd = schedule.endDate ? new Date(schedule.endDate) : scheduleStart;

            if (start && end) {
                return scheduleEnd >= start && scheduleStart <= end;
            } else if (start) {
                return scheduleEnd >= start;
            } else if (end) {
                return scheduleStart <= end;
            } else {
                return true;
            }
        });

        setFilteredSchedules(filtered);
    };

    useEffect(() => {
        filterSchedulesByDate(currentFilteredDateRange)
    }, [currentFilteredDateRange, props.schedules])

    const handleCloseModal = () => {
        setShowScheduleDetails(false)
        props.onClose()
        setCurrentFilteredDateRange([null, null])
    }

    const navigateToCalendarView = () => {
        navigate("/qualitychecks?view-type=calendar&checklistId=" + props.checklist?.idChecklist)

    }

    return <Modal
        isOpen={props.isOpen}
        onClose={handleCloseModal}
        useWidth>
        <Modal.Content>
            {!showScheduleDetails && <>
                <Row alignItems="center">
                    <DateRangeBox
                        height={36}
                        width={300}
                        startDateLabel={t("Start Date")}
                        endDateLabel={t("End Date")}
                        showClearButton
                        stylingMode="outlined"
                        value={currentFilteredDateRange}
                        onValueChanged={(e) => {
                            setCurrentFilteredDateRange(e.value)
                        }}
                    />
                    <Row.End>
                        <Button
                            text={t("New schedule")}
                            width={200}
                            type="default"
                            className="border-radius-xsmall"
                            onClick={handleCreateNewSchedule} />
                    </Row.End>
                </Row>
                {filteredSchedules.length === 0 && <span>{t("No schedules yet")}</span>}
                <div style={{ maxHeight: 400, overflowY: "scroll" }}>
                    {filteredSchedules.map((e, i) =>
                        <div key={i} className="p-02">
                            <ScheduleRow
                                key={i}
                                schedule={e}
                                handleCalendarClicked={navigateToCalendarView}
                                handleEditSchedule={handleEditSchedule}
                                handleDeleteSchedule={handleDeleteChecklistSchedule} />
                        </div>
                    )}
                </div>
            </>}

            {showScheduleDetails && <>
                <Row>
                    <IconButtonGoBack onClick={() => setShowScheduleDetails(false)} />
                    {formData?.idChecklistSchedule &&
                        <Row.End>
                            <IconButtonCalendar onClick={navigateToCalendarView} />
                            <IconButtonDelete disabled={!formData?.idChecklistSchedule} onClick={() => handleDeleteChecklistSchedule(formData?.idChecklist, formData?.idChecklistSchedule)} />
                        </Row.End>}
                </Row>
                <ScheduleForm
                    loading={loading}
                    checklists={props.checklist ? [props.checklist] : []}
                    initialData={formData}
                    handleChange={handleChange}
                    validationErrors={validationErrors}
                    usageContext="checklist"
                    resetValidationErrors={() => clearValidation()} />
            </>}
        </Modal.Content>
        <Modal.Footer>
            {showScheduleDetails && <Button
                className="border-radius-small"
                type="success"
                text={t("Save")}
                width={100}
                disabled={loading}
                onClick={handleSaveChecklistSchedule} />}
            <Button
                hoverStateEnabled
                className="border-radius-small"
                text={t("Close")}
                width={100}
                disabled={loading}
                onClick={handleCloseModal} />
        </Modal.Footer>
    </Modal>
}